import React from 'react';

import Button from '@material-ui/core/Button';
import CloseIcon from '@material-ui/icons/Close';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';

import FullScreenDialog, { FullScreenDialogProps } from '../FullScreenDialog';
import useCompanyContext from '../../hooks/useCompanyContext';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: theme.spacing(6),
  },
  mainContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  merchantLogo: {
    height: 120,
    width: 120,
    borderRadius: '50%',
    objectFit: 'cover',
    boxShadow: '0 2px 10px rgba(199, 199, 199, 0.5)',
  },
  header: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(5),
    textAlign: 'center',
    fontWeight: theme.typography.fontWeightRegular,
  },
  box: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
  },
  close: {
    marginRight: 'auto',
  },
  closeIcon: {
    color: theme.palette.common.black,
  },
  actionContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: theme.spacing(5),
  },
  logoContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  text: {
    marginBottom: theme.spacing(2),
  },
  actionButton: {
    width: 200,
  },
}));

type Props = Pick<FullScreenDialogProps, 'open'> & {
  onClose: () => void;
  onSuccess: () => void;
};

export default function HowToUseAppDialog({ open, onClose, onSuccess }: Props) {
  const classes = useStyles();
  const { company } = useCompanyContext();

  if (!company) {
    return null;
  }

  return (
    <FullScreenDialog open={open}>
      <div className={classes.box}>
        <div className={classes.close}>
          <IconButton onClick={onClose}>
            <CloseIcon className={classes.closeIcon} />
          </IconButton>
        </div>
      </div>
      <div className={classes.logoContainer}>
        <img
          src={company.logo?.versions.xl}
          className={classes.merchantLogo}
          alt="flex reward"
        />
        <Typography variant="h4" className={classes.header}>
          HOW TO USE THIS APP
        </Typography>
        <Grid item xs={8}>
          <Typography variant="h6" className={classes.title} gutterBottom>
            1. ADD MEMBERS
          </Typography>
          <Typography variant="body2" className={classes.text} gutterBottom>
            Tap <b>+Add Member</b> to register new customers. A Welcome Email
            will be sent with instructions on how to access their new account
            via the Flex Rewards app or the online WebApp.
          </Typography>
          <Typography variant="h6" className={classes.title} gutterBottom>
            2. LOOKUP MEMBERS
          </Typography>
          <Typography variant="body2" className={classes.text} gutterBottom>
            Tap a name to display member account details. Update details by
            using the <b>Add Stamps</b> and <b>Redeem Reward</b> buttons.
          </Typography>
          <Typography variant="h6" className={classes.title} gutterBottom>
            3. SHOW QR CODES
          </Typography>
          <Typography variant="body2" className={classes.text} gutterBottom>
            Show an <b>Add Stamp Code</b> to allow customers to self-register
            and collect stamps from their phone. The Flex Rewards app does not
            need to be installed for the scan to work. Select{' '}
            <b>QR Code Only</b> mode from settings to only display{' '}
            <b>Add Stamp Codes</b> without access to the full membership list.
          </Typography>
          <div className={classes.actionContainer}>
            <Button
              color="primary"
              size="large"
              className={classes.actionButton}
              onClick={() => onSuccess()}
            >
              Continue
            </Button>
          </div>
        </Grid>
      </div>
    </FullScreenDialog>
  );
}
